import React from "react";
import { TOKEN_STATE } from "src/hooks/common/useTokenValidation";


const ShieldOffIcon = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);

const ClockOffIcon = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);

const KeyOffIcon = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);

// ─── Config per error type ────────────────────────────────────────────────────

const ERROR_CONFIG = {
  [TOKEN_STATE.MISSING]: {
    icon: KeyOffIcon,
    color: "#f59e0b",        // amber
    bgColor: "#fffbeb",
    borderColor: "#fde68a",
    title: "Accès non autorisé",
    subtitle: "Aucun token d'authentification trouvé",
    description:
      "Cette page nécessite un token d'accès valide. Veuillez utiliser le lien fourni par votre administrateur ou vous connecter depuis la page principale.",
    actionLabel: "Retour à la connexion",
    actionHref: "https://dev.lite.lewootrack.com/",
    showRetry: false,
    code: "AUTH_TOKEN_MISSING",
  },
  [TOKEN_STATE.EXPIRED]: {
    icon: ClockOffIcon,
    color: "#ef4444",        // red
    bgColor: "#fef2f2",
    borderColor: "#fecaca",
    title: "Session expirée",
    subtitle: "Votre token d'accès a expiré",
    description:
      "La durée de validité de votre session est dépassée. Veuillez vous reconnecter pour obtenir un nouveau token et continuer à utiliser l'application.",
    actionLabel: "Se reconnecter",
    actionHref: "https://dev.lite.lewootrack.com/",
    showRetry: false,
    code: "AUTH_TOKEN_EXPIRED",
  },
  [TOKEN_STATE.INVALID]: {
    icon: ShieldOffIcon,
    color: "#dc2626",        // deep red
    bgColor: "#fef2f2",
    borderColor: "#fecaca",
    title: "Token invalide",
    subtitle: "Le token fourni n'est pas reconnu pour ce Navigateur",
    description:
      "Le token d'authentification fourni est invalide ou a été révoqué. Contactez votre administrateur pour obtenir un nouveau lien d'accès.",
    actionLabel: "Retour à l'accueil",
    actionHref: "https://dev.lite.lewootrack.com/",
    showRetry: true,
    code: "AUTH_TOKEN_INVALID",
  },
};


/**
 * @param {{ tokenState: string, onRetry?: () => void }} props
 */
const TokenErrorPage = ({ tokenState, onRetry }) => {
  const cfg = ERROR_CONFIG[tokenState];

  if (!cfg) return null;

  const Icon = cfg.icon;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f8fafc",
        padding: "24px",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "480px",
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          border: `1px solid ${cfg.borderColor}`,
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        {/* Top accent bar */}
        <div style={{ height: "4px", backgroundColor: cfg.color }} />

        <div style={{ padding: "40px 36px 36px" }}>
          {/* Icon */}
          <div
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: cfg.bgColor,
              border: `2px solid ${cfg.borderColor}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: cfg.color,
              marginBottom: "24px",
            }}
          >
            <Icon />
          </div>

          {/* Error code badge */}
          <span
            style={{
              display: "inline-block",
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.08em",
              color: cfg.color,
              backgroundColor: cfg.bgColor,
              border: `1px solid ${cfg.borderColor}`,
              borderRadius: "4px",
              padding: "2px 8px",
              marginBottom: "12px",
              textTransform: "uppercase",
            }}
          >
            {cfg.code}
          </span>

          {/* Title */}
          <h1
            style={{
              fontSize: "22px",
              fontWeight: 700,
              color: "#111827",
              margin: "0 0 6px",
              lineHeight: 1.3,
            }}
          >
            {cfg.title}
          </h1>

          {/* Subtitle */}
          <p
            style={{
              fontSize: "14px",
              fontWeight: 500,
              color: cfg.color,
              margin: "0 0 16px",
            }}
          >
            {cfg.subtitle}
          </p>

          {/* Description */}
          <p
            style={{
              fontSize: "14px",
              color: "#6b7280",
              lineHeight: 1.65,
              margin: "0 0 28px",
            }}
          >
            {cfg.description}
          </p>

          {/* Divider */}
          <div
            style={{
              height: "1px",
              backgroundColor: "#f3f4f6",
              margin: "0 0 24px",
            }}
          />

          {/* Actions */}
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <a
              href={cfg.actionHref}
              style={{
                flex: 1,
                minWidth: "140px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                backgroundColor: cfg.color,
                color: "#ffffff",
                borderRadius: "8px",
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: 600,
                textDecoration: "none",
                border: "none",
                cursor: "pointer",
                transition: "opacity 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              {cfg.actionLabel}
            </a>

            {cfg.showRetry && onRetry && (
              <button
                type="button"
                onClick={onRetry}
                style={{
                  flex: 1,
                  minWidth: "140px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  backgroundColor: "#ffffff",
                  color: "#374151",
                  borderRadius: "8px",
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: 600,
                  textDecoration: "none",
                  border: "1px solid #d1d5db",
                  cursor: "pointer",
                  transition: "background-color 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 .49-3.5" />
                </svg>
                Réessayer
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "14px 36px",
            backgroundColor: "#f9fafb",
            borderTop: "1px solid #f3f4f6",
          }}
        >
          <p style={{ fontSize: "12px", color: "#9ca3af", margin: 0 }}>
            Si le problème persiste, contactez votre administrateur système.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TokenErrorPage;
